import { Box, Grow, Skeleton, Stack, Typography } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { motion } from 'framer-motion';
import { FC, useEffect, useMemo, useState } from 'react';

interface ChartItem {
  label: string;
  value: number;
}

interface Props {
  title: string;
  subtitle: string;
  data: ChartItem[];
  legendLabels?: string[];
  loading?: boolean;
  hasTimedOut?: boolean;
}

const COLORS = ['#A78BFA', '#60A5FA', '#FDBA74', '#86EFAC', '#FCA5A5'];

export const AverageTicketsPie: FC<Props> = ({
  title,
  subtitle,
  data,
  legendLabels,
  loading = false,
  hasTimedOut = false,
}) => {
  const [chartKey, setChartKey] = useState(0);
  const [ready, setReady] = useState(false);

  const legendItems = useMemo<ChartItem[]>(() => {
    if (legendLabels && legendLabels.length > 0) {
      const valueByLabel = new Map(data.map((item) => [item.label, item.value]));
      return legendLabels.map((label) => ({
        label,
        value: valueByLabel.get(label) ?? 0,
      }));
    }
    return data;
  }, [data, legendLabels]);

  const hasChartData = useMemo(() => data.some((item) => item.value > 0), [data]);
  const skeletonRows = useMemo(() => {
    if (legendLabels && legendLabels.length > 0) return legendLabels.length;
    return data.length > 0 ? data.length : 5;
  }, [data.length, legendLabels]);

  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => setReady(true), 500);
      return () => clearTimeout(timer);
    } else {
      setReady(false);
    }
  }, [loading]);

  useEffect(() => {
    if (!loading && data.length > 0) {
      setChartKey((k) => k + 1);
    }
  }, [JSON.stringify(data), loading]);

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: '#fff',
        borderRadius: 2,
        boxShadow: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Title */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
        {title}
      </Typography>

      {/* Chart */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 200 }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Skeleton variant="circular" width={180} height={180} />
          </Box>
        ) : hasTimedOut || !hasChartData ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: '#6B7280', textAlign: 'center', mb: 1 }}
            >
              No data available
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: '#9CA3AF', textAlign: 'center' }}
            >
              {hasTimedOut ? 'Request timed out' : 'No records found'}
            </Typography>
          </Box>
        ) : (
          <motion.div
            key={chartKey}
            initial={{ opacity: 0, rotate: -180 }}
            animate={{ opacity: 1, rotate: 0 }}
            transition={{
              duration: 3.5,
              ease: [0.16, 1, 0.3, 1],
            }}
          >
            <PieChart
              series={[
                {
                  data: legendItems.map((item, index) => ({
                    id: index,
                    label: item.label,
                    value: item.value,
                    color: COLORS[index % COLORS.length],
                  })),
                },
              ]}
              skipAnimation={false}
              slots={{ legend: () => null }}
              slotProps={{ tooltip: { trigger: 'item' } }}
              margin={{ top: 5, bottom: 5, left: 5, right: 5 }}
              sx={{
                width: '100%',
                height: '100%',
                minHeight: 200,
              }}
            />
          </motion.div>
        )}
      </Box>

      {/* Legend Subtitle */}
      <Grow in={ready && !loading && legendItems.length > 0} timeout={600} style={{ transitionDelay: '400ms' }}>
        <Typography
          variant="subtitle2"
          sx={{
            mb: -1,
            fontSize: 12,
            justifyContent: 'center',
            display: 'flex',
            color: '#6B7280',
          }}
        >
          {subtitle}
        </Typography>
      </Grow>

      {/* Legend */}
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {loading
          ? Array.from({ length: skeletonRows }).map((_, index) => (
              <Box
                key={`skeleton-${index}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pr: 2,
                  pl: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Skeleton variant="circular" width={16} height={16} />
                  <Skeleton variant="text" width={70} height={16} />
                </Box>
                <Skeleton variant="text" width={25} height={16} />
              </Box>
            ))
          : legendItems.map((item, index) => (
              <Box
                key={item.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pr: 2,
                  pl: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box
                    sx={{
                      width: '1rem',
                      height: '1rem',
                      borderRadius: '50%',
                      backgroundColor: COLORS[index % COLORS.length],
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: '#6B7280', fontSize: 12, fontWeight: 500 }}
                  >
                    {item.label}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{ color: '#6B7280', fontSize: 12, fontWeight: 500 }}
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
      </Stack>
    </Box>
  );
};
